import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import type { CommissionPeriodType } from './types';

const PERIOD_LABELS: Record<CommissionPeriodType, string> = {
    month: 'Mensal',
    bimonth: 'Bimestral',
    quarter: 'Trimestral',
    quadrimester: 'Quadrimestral',
    semester: 'Semestral',
    year: 'Anual',
};

const INDEX_OPTIONS: Record<CommissionPeriodType, { value: number; label: string }[]> = {
    month: Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(2000, i, 1).toLocaleString('pt-BR', { month: 'long' }),
    })),
    bimonth: [1, 2, 3, 4, 5, 6].map((n) => ({ value: n, label: `${n}º bimestre` })),
    quarter: [1, 2, 3, 4].map((n) => ({ value: n, label: `${n}º trimestre` })),
    quadrimester: [1, 2, 3].map((n) => ({ value: n, label: `${n}º quadrimestre` })),
    semester: [
        { value: 1, label: '1º semestre' },
        { value: 2, label: '2º semestre' },
    ],
    year: [{ value: 1, label: 'Ano completo' }],
};

type Props = {
    period: CommissionPeriodType;
    year: number;
    index: number;
    yearOptions: number[];
    periodLabel?: string;
    periodRange?: string;
    onPeriodChange: (p: CommissionPeriodType) => void;
    onYearChange: (y: number) => void;
    onIndexChange: (i: number) => void;
};

export default function OrgChartFilters({
    period,
    year,
    index,
    yearOptions,
    periodLabel,
    periodRange,
    onPeriodChange,
    onYearChange,
    onIndexChange,
}: Props) {
    const options = INDEX_OPTIONS[period];
    const maxIndex = options.length;
    const currentLabel = options.find((o) => o.value === index)?.label ?? '';

    function prev() {
        if (index > 1) {
            onIndexChange(index - 1);
        } else if (year > yearOptions[0]) {
            onYearChange(year - 1);
            onIndexChange(maxIndex);
        }
    }

    function next() {
        if (index < maxIndex) {
            onIndexChange(index + 1);
        } else if (year < yearOptions[yearOptions.length - 1]) {
            onYearChange(year + 1);
            onIndexChange(1);
        }
    }

    const isAtStart = index === 1 && year === yearOptions[0];
    const isAtEnd   = index === maxIndex && year === yearOptions[yearOptions.length - 1];

    return (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
            {/* Tipo de período */}
            <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-100 p-1">
                {(Object.entries(PERIOD_LABELS) as [CommissionPeriodType, string][]).map(([val, lbl]) => (
                    <button
                        key={val}
                        type="button"
                        onClick={() => onPeriodChange(val)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                            period === val
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {lbl}
                    </button>
                ))}
            </div>

            {/* Ano */}
            <select
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-primary focus:outline-none"
                value={year}
                onChange={(e) => onYearChange(Number(e.target.value))}
            >
                {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                ))}
            </select>

            {/* Navegação ← período → */}
            {period !== 'year' && (
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={prev}
                        disabled={isAtStart}
                        className="flex size-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <PiCaretLeftBold className="size-3.5" />
                    </button>

                    <select
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm focus:border-primary focus:outline-none"
                        value={index}
                        onChange={(e) => onIndexChange(Number(e.target.value))}
                    >
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    <button
                        type="button"
                        onClick={next}
                        disabled={isAtEnd}
                        className="flex size-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <PiCaretRightBold className="size-3.5" />
                    </button>
                </div>
            )}

            {/* Label resumido do período */}
            {periodLabel && (
                <div className="ml-auto text-right">
                    <p className="text-xs font-semibold text-gray-700">{periodLabel}</p>
                    {periodRange && <p className="text-[11px] text-gray-400">{periodRange}</p>}
                </div>
            )}
        </div>
    );
}
